/**
 * Velt SDK adapter.
 * This is the ONLY place where @veltdev/types may be imported or window.Velt accessed.
 *
 * Responsibilities:
 * - Wrap all Velt SDK calls with error handling
 * - Provide high-level async functions for SDK operations
 * - Manage singleton subscription pattern
 *
 * Architectural Rules:
 * - MAY import @veltdev/types
 * - MAY access window.Velt
 * - MUST wrap all SDK calls in try-catch
 * - MUST NOT import editor types
 */
import { __awaiter } from "tslib";
import { areMapsEqual } from '@/utils/common';
import { Console } from '@/utils/console';
/**
 * Global state for singleton subscription pattern.
 */
let isSubscribed = false;
let selectedCommentsMap = new Map();
const subscribers = new Map();
/**
 * Gets Velt comment element from SDK.
 * Returns null if SDK is not available.
 */
export const getCommentElement = () => {
    try {
        // Access window.Velt safely
        const velt = window.Velt;
        if (velt === null || velt === void 0 ? void 0 : velt.getCommentElement) {
            return velt.getCommentElement();
        }
        return null;
    }
    catch (error) {
        Console.catch('[VeltAdapter] Error getting comment element:', error);
        return null;
    }
};
/**
 * Adds a comment via Velt SDK.
 * Returns the created annotation, or null on failure.
 */
export const addVeltComment = (params) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const commentElement = getCommentElement();
        if (!commentElement) {
            return null;
        }
        const result = yield commentElement.addManualComment({
            context: params.context,
            location: params.location,
        });
        if (!result) {
            return null;
        }
        // Result should contain the annotation
        return {
            annotation: result,
        };
    }
    catch (error) {
        Console.catch('[VeltAdapter] Error adding comment:', error);
        return null;
    }
});
/**
 * Updates annotation context in Velt SDK.
 */
export const updateAnnotationContext = (annotationId, context) => {
    try {
        const commentElement = getCommentElement();
        if (!commentElement) {
            return;
        }
        commentElement.updateContext(annotationId, context);
    }
    catch (error) {
        Console.catch('[VeltAdapter] Error updating context:', error);
    }
};
/**
 * Sets up the singleton subscription to getSelectedComments.
 * This is separated out so it can be retried when commentElement becomes available.
 */
const setupSelectedCommentsSubscription = () => {
    const commentElement = getCommentElement();
    if (!(commentElement === null || commentElement === void 0 ? void 0 : commentElement.getSelectedComments)) {
        return false;
    }
    try {
        const observable = commentElement.getSelectedComments();
        if (!(observable === null || observable === void 0 ? void 0 : observable.subscribe)) {
            return false;
        }
        // Subscribe returns an RxJS Subscription object with an unsubscribe method
        const subscription = observable.subscribe((comments) => {
            const selectedMap = new Map();
            comments === null || comments === void 0 ? void 0 : comments.forEach((comment) => {
                if (comment === null || comment === void 0 ? void 0 : comment.annotationId) {
                    selectedMap.set(comment.annotationId, true);
                }
            });
            // Skip update if maps are equal
            if (!areMapsEqual(selectedMap, selectedCommentsMap)) {
                selectedCommentsMap = selectedMap;
                // Convert Map to Set<string> for callback (matching Tiptap's signature)
                const selectedIds = new Set();
                for (const [annotationId] of selectedMap) {
                    selectedIds.add(annotationId);
                }
                // Notify all subscribers with Set<string>
                subscribers.forEach((subscriber) => {
                    if (typeof subscriber === 'function') {
                        try {
                            subscriber(selectedIds);
                        }
                        catch (error) {
                            Console.catch('[VeltAdapter] Error in subscriber callback:', error);
                        }
                    }
                });
            }
        });
        // Check if subscription was successful by verifying the subscription object
        // has an unsubscribe method (RxJS Subscription pattern)
        if (subscription && typeof subscription.unsubscribe === 'function') {
            isSubscribed = true;
            return true;
        }
    }
    catch (error) {
        Console.catch('[VeltAdapter] Error setting up getSelectedComments subscription:', error);
    }
    return false;
};
/**
 * Subscribes to selected annotations from Velt SDK.
 * Uses singleton pattern to avoid multiple subscriptions.
 * Returns unsubscribe function.
 *
 * Matches Tiptap's signature: callback receives Set<string> of selected annotation IDs.
 */
export const subscribeToSelectedAnnotations = (callback, editorId) => {
    try {
        // Register this editor's callback first
        subscribers.set(editorId, callback);
        // Try to set up singleton subscription if not already subscribed
        if (!isSubscribed) {
            setupSelectedCommentsSubscription();
        }
        // Return unsubscribe function
        return () => {
            subscribers.delete(editorId);
        };
    }
    catch (error) {
        Console.catch('[VeltAdapter] Error subscribing to selected annotations:', error);
        return () => {
            // No-op unsubscribe on error
        };
    }
};
/**
 * Attempts to set up the selected comments subscription if not already done.
 * This should be called when we know the Velt SDK is ready.
 * Returns true if subscription is now active.
 */
export const ensureSelectedCommentsSubscription = () => {
    if (isSubscribed) {
        return true;
    }
    return setupSelectedCommentsSubscription();
};
